import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from '@/lib/logger';

/**
 * PWA update orchestrator (P-11, 2026-04-18).
 *
 * Strategy (how big PWAs do it):
 * 1. vite-plugin-pwa runs in `registerType: "autoUpdate"` so Workbox activates
 *    the new service worker in the background silently (skipWaiting + clientsClaim).
 * 2. After activation, cached assets of the OLD version still serve the open
 *    tabs until a navigation replaces the HTML. We force that replacement by
 *    reloading on the NEXT React Router route change — feels like a normal
 *    navigation to the user, no banner.
 * 3. If the user sits on the same route for too long (15+ min), or returns
 *    from a long background (10+ min with tab hidden), we trigger the reload
 *    proactively. UpdateSnackbar is a last-resort UI fallback.
 *
 * This replaces the legacy `useAppVersion` + `UpdatePrompt` + database
 * `force_update` flag — that whole flow was v0 boilerplate, ~500 lines.
 */

const BACKGROUND_RELOAD_THRESHOLD_MS = 10 * 60 * 1000;   // 10 min hidden → reload on return
const IDLE_SNACKBAR_THRESHOLD_MS = 15 * 60 * 1000;        // 15 min on same route → show banner

export function usePWAUpdate() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const location = useLocation();
  const lastNavigationAt = useRef<number>(Date.now());
  const hiddenSince = useRef<number | null>(null);
  const hasUpdateRef = useRef(false);

  // Keep ref in sync so event listeners always see fresh value.
  useEffect(() => { hasUpdateRef.current = hasUpdate; }, [hasUpdate]);

  // Listen for SW update availability.
  useEffect(() => {
    const onUpdate = () => {
      logger.log('🔄 [usePWAUpdate] new version available — will reload on next navigation');
      setHasUpdate(true);
    };
    window.addEventListener('sw-update-available', onUpdate);
    return () => window.removeEventListener('sw-update-available', onUpdate);
  }, []);

  // Reload on route change if an update is pending.
  useEffect(() => {
    // Skip the first render (initial mount).
    if (!hasUpdateRef.current) {
      lastNavigationAt.current = Date.now();
      return;
    }
    logger.log('🔄 [usePWAUpdate] route change detected with pending update — reloading');
    // Defer to let React finish committing the route change, then reload.
    // User perceives it as navigation with a slightly slower transition.
    setTimeout(() => window.location.reload(), 0);
  }, [location.pathname]);

  // Reload on tab returning from long background.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        hiddenSince.current = Date.now();
        return;
      }
      // Tab became visible again.
      if (hiddenSince.current && hasUpdateRef.current) {
        const hiddenMs = Date.now() - hiddenSince.current;
        if (hiddenMs >= BACKGROUND_RELOAD_THRESHOLD_MS) {
          logger.log(`🔄 [usePWAUpdate] returning from ${Math.round(hiddenMs / 60000)}min background with pending update — reloading`);
          window.location.reload();
        }
      }
      hiddenSince.current = null;
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Fallback snackbar if user sits on same route for a long time with pending update.
  useEffect(() => {
    if (!hasUpdate) {
      setShowSnackbar(false);
      return;
    }
    const timer = setTimeout(() => {
      logger.log('🔄 [usePWAUpdate] idle on same route with pending update — showing snackbar');
      setShowSnackbar(true);
    }, IDLE_SNACKBAR_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, [hasUpdate, location.pathname]);

  const applyUpdate = () => window.location.reload();
  const dismissSnackbar = () => setShowSnackbar(false);

  return { hasUpdate, showSnackbar, applyUpdate, dismissSnackbar };
}
